import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  Chip,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimerOffIcon from "@mui/icons-material/TimerOff";

const SessionExpiredDialog = ({ open, onReauthenticate, onCancel, username, reason = "expired" }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300);

  useEffect(() => {
    if (!open) {
      setTimeRemaining(300);
      setPassword("");
      setError("");
      return;
    }

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onCancel();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, onCancel]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getProgressValue = () => {
    return ((300 - timeRemaining) / 300) * 100;
  };

  const getTimerColor = () => {
    if (timeRemaining <= 60) return "error";
    if (timeRemaining <= 120) return "warning";
    return "primary";
  };

  const getReasonMessage = () => {
    if (reason === "idle") {
      return "You have been inactive for 30 minutes. Please enter your password to continue.";
    }
    return "Your session has expired. Please enter your password to continue.";
  };

  const getReasonChip = () => {
    if (reason === "idle") {
      return (
        <Chip
          icon={<TimerOffIcon />}
          label="Idle Timeout"
          color="warning"
          size="small"
          sx={{ mb: 2 }}
        />
      );
    }
    return (
      <Chip
        icon={<AccessTimeIcon />}
        label="Session Expired"
        color="error"
        size="small"
        sx={{ mb: 2 }}
      />
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!password) {
      setError("Password is required");
      return;
    }

    setIsLoading(true);
    try {
      await onReauthenticate(password);
      setPassword("");
      setTimeRemaining(300);
    } catch (err) {
      setError(err.message || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setPassword("");
    setError("");
    setTimeRemaining(300);
    onCancel();
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleCancel();
        }
      }}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <LockOutlinedIcon color="primary" />
            <Typography variant="h6">Authentication Required</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <AccessTimeIcon color={getTimerColor()} fontSize="small" />
            <Typography 
              variant="body2" 
              color={getTimerColor()}
              fontWeight="bold"
            >
              {formatTime(timeRemaining)}
            </Typography>
          </Box>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={getProgressValue()} 
          color={getTimerColor()}
          sx={{ mt: 1 }}
        />
      </DialogTitle>
      <DialogContent>
        {getReasonChip()}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {getReasonMessage()}
        </Typography>
        <Typography variant="caption" color="error" sx={{ mb: 2, display: "block" }}>
          You will be automatically logged out in {formatTime(timeRemaining)}
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="username"
            label="Username"
            value={username || ""}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError("");
            }}
            error={!!error}
            helperText={error}
            disabled={isLoading}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleCancel} 
          disabled={isLoading}
          color="inherit"
        >
          Logout
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !password}
        >
          {isLoading ? <CircularProgress size={24} /> : "Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiredDialog;